import { registerAssignmentEvents } from "./assignmentActions.js";
import { initDatalistHandlers } from "./datalistHandlers.js";
import { initDashboardTable } from "./datatableInit.js";
import {
    refreshAssignments,
    startAutoRefresh,
    stopAutoRefresh,
    startBroadcastRefresh,
    stopBroadcastRefresh,
} from "./refresh.js";
import { goToDate, toggleAddForm, toggleLoadingLocal } from "./dom.js";
import { toggleRecurrenceFields, toggleDaysOfWeek } from "./recurrence.js";
import { showErrorMessage, showSuccessMessage } from "./messages.js";

function datalistHasValue(listId, value) {
    const normalized = (value || "").trim().toLowerCase();
    if (!normalized) return true;

    const list = document.getElementById(listId);
    if (!list) return true;

    return Array.from(list.options).some(
        (option) => option.value?.trim().toLowerCase() === normalized,
    );
}

function askAutoAddReferences(missingLabels) {
    if (!missingLabels.length) return Promise.resolve(false);

    const message =
        `Les valeurs suivantes ne sont pas dans les données de référence : ${missingLabels.join(", ")}. ` +
        "Voulez-vous les ajouter à la base de données ?";

    if (typeof window.showConfirmationModal === "function") {
        return new Promise((resolve) => {
            window.showConfirmationModal({
                title: "Ajouter aux références ?",
                message,
                yesText: "Oui",
                noText: "Non",
                yesClass: "bg-blue-600 hover:bg-blue-700",
                noClass: "bg-gray-500 hover:bg-gray-600",
                onYes: () => resolve(true),
                onNo: () => resolve(false),
                onCancel: () => resolve(false),
            });
        });
    }

    return Promise.resolve(window.confirm(message));
}

if (!window.dashboardScriptLoaded) {
    window.dashboardScriptLoaded = true;

    registerAssignmentEvents();
    initDatalistHandlers();

    const initDashboardPage = () => {
        if (!document.getElementById("dashboard-table")) return;

        const initDashboardTableWhenReady = (attempt = 0) => {
            const table = document.getElementById("dashboard-table");
            const dataTableReady =
                window.jQuery &&
                window.jQuery.fn?.dataTable &&
                table &&
                table.isConnected &&
                table.parentNode &&
                table.tHead &&
                table.tBodies &&
                table.tBodies.length;

            if (dataTableReady) {
                try {
                    initDashboardTable();
                } finally {
                    delete document.body.dataset.dashboardDtInitPending;
                }
                return;
            }

            if (attempt >= 20) {
                delete document.body.dataset.dashboardDtInitPending;
                return;
            }

            setTimeout(() => initDashboardTableWhenReady(attempt + 1), 100);
        };

        if (!document.body.dataset.dashboardDtInitPending) {
            document.body.dataset.dashboardDtInitPending = "true";
            initDashboardTableWhenReady();
        }
        stopBroadcastRefresh();
        startBroadcastRefresh();

        const addForm = document.querySelector("#addFormContainer form");
        if (addForm && !addForm.dataset.ajaxBound) {
            addForm.dataset.ajaxBound = "true";
            addForm.addEventListener("submit", async (event) => {
                event.preventDefault();

                try {
                    const formData = new FormData(addForm);
                    const sender = String(formData.get("sender") || "").trim();
                    const destination = String(
                        formData.get("destination") || "",
                    ).trim();

                    const missingLabels = [];
                    if (
                        sender &&
                        !datalistHasValue("deliveryRunsListForm", sender)
                    ) {
                        missingLabels.push(`Expéditeur: ${sender}`);
                    }
                    if (
                        destination &&
                        !datalistHasValue("destinationsListForm", destination)
                    ) {
                        missingLabels.push(`Destinataire: ${destination}`);
                    }

                    let autoAddReferences = false;
                    if (missingLabels.length) {
                        autoAddReferences =
                            await askAutoAddReferences(missingLabels);
                    }

                    formData.set(
                        "auto_add_references",
                        autoAddReferences ? "1" : "0",
                    );

                    toggleLoadingLocal(true);

                    const response = await fetch(addForm.action, {
                        method: "POST",
                        headers: {
                            "X-Requested-With": "XMLHttpRequest",
                            Accept: "application/json",
                            "X-CSRF-TOKEN": document.querySelector(
                                'meta[name="csrf-token"]',
                            ).content,
                        },
                        body: formData,
                    });

                    const payload = await response.json();
                    if (!response.ok) {
                        const firstError = payload?.errors
                            ? Object.values(payload.errors)[0]?.[0]
                            : null;
                        throw new Error(
                            firstError || payload?.message || "Erreur inconnue",
                        );
                    }

                    showSuccessMessage(
                        payload?.message || "Livraison créée avec succès.",
                    );
                    if (payload?.warning) {
                        showErrorMessage(payload.warning);
                    }
                    if (payload?.reference_notice) {
                        showSuccessMessage(payload.reference_notice);
                    }
                    await refreshAssignments();
                    addForm.reset();
                    toggleRecurrenceFields();
                    toggleDaysOfWeek();
                } catch (error) {
                    showErrorMessage(
                        error?.message || "Erreur lors de l'ajout.",
                    );
                } finally {
                    toggleLoadingLocal(false);
                }
            });
        }

        const datePicker = document.getElementById("datePicker");
        if (datePicker && !datePicker.dataset.bound) {
            datePicker.dataset.bound = "true";
            datePicker.addEventListener("change", (event) => {
                const value = event.target?.value;
                if (value) {
                    goToDate(value);
                }
            });
        }

        const tableWrapper = document.getElementById("dashboardTableWrapper");
        const scrollBar = document.getElementById("dashboardTableScroll");
        const scrollInner = document.getElementById(
            "dashboardTableScrollInner",
        );

        if (
            tableWrapper &&
            scrollBar &&
            scrollInner &&
            !tableWrapper.dataset.scrollBound
        ) {
            tableWrapper.dataset.scrollBound = "true";
            let isSyncing = false;

            // Always keep the mirror bar fixed at the viewport bottom,
            // aligned to the table wrapper, while the table is on screen.
            const updateScrollBar = () => {
                const contentWidth = tableWrapper.scrollWidth;
                scrollInner.style.width = `${contentWidth}px`;

                const needsScroll =
                    tableWrapper.scrollWidth > tableWrapper.clientWidth;
                const wRect = tableWrapper.getBoundingClientRect();
                const inView =
                    wRect.bottom > 0 && wRect.top < window.innerHeight;

                if (!needsScroll || !inView) {
                    scrollBar.style.display = "none";
                    return;
                }

                // Sync horizontal position before making visible
                if (!isSyncing) {
                    isSyncing = true;
                    scrollBar.scrollLeft = tableWrapper.scrollLeft;
                    isSyncing = false;
                }

                scrollBar.style.display = "block";
                scrollBar.style.position = "fixed";
                scrollBar.style.bottom = "0";
                scrollBar.style.left = wRect.left + "px";
                scrollBar.style.width = wRect.width + "px";
                scrollBar.style.margin = "0";
                scrollBar.style.zIndex = "50";
            };

            // User drags the table horizontally → move mirror bar
            tableWrapper.addEventListener("scroll", () => {
                if (isSyncing) return;
                isSyncing = true;
                scrollBar.scrollLeft = tableWrapper.scrollLeft;
                isSyncing = false;
            });

            // User drags the mirror bar → move the table
            scrollBar.addEventListener("scroll", () => {
                if (isSyncing) return;
                isSyncing = true;
                tableWrapper.scrollLeft = scrollBar.scrollLeft;
                isSyncing = false;
            });

            window.addEventListener("scroll", updateScrollBar, {
                passive: true,
            });

            const resizeObserver = new ResizeObserver(updateScrollBar);
            resizeObserver.observe(tableWrapper);
            const table = document.getElementById("dashboard-table");
            if (table) {
                resizeObserver.observe(table);
            }

            window.addEventListener("resize", updateScrollBar);
            setTimeout(updateScrollBar, 0);
        }
    };

    document.addEventListener("DOMContentLoaded", initDashboardPage);
    document.addEventListener("livewire:navigated", initDashboardPage);

    window.toggleAddForm = toggleAddForm;
    window.toggleRecurrenceFields = toggleRecurrenceFields;
    window.toggleDaysOfWeek = toggleDaysOfWeek;
}
